import { useState, useEffect } from 'react';
import { getLessons } from '@services/repService';

/**
 * All published lessons, DB-backed — the single source of truth for
 * lesson titles now that terminalTokens.js's LESSON_NAMES is gone (see
 * that file's own removal note).
 */
export const useLessons = () => {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  // Prompt 6, item 1: a failed fetch used to be indistinguishable from an
  // empty-but-successful one — both left `lessons: []`, which every
  // lessonName()/repCountFor()/lessonNumberFor() fallback quietly renders
  // as if it were real content ("Lesson 11", "Rep 2 of 5"). A learner
  // seeing that has no way to tell "the app is broken right now" from
  // "this is just what the app looks like". `error` makes that visible
  // to the caller instead of swallowing it.
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getLessons()
      .then(data => { if (!cancelled) { setLessons(data); setError(null); } })
      .catch((err) => {
        // fetch() itself rejects with a TypeError when it never reaches a
        // server at all (connection refused, DNS failure, CORS block) —
        // that's a different failure than repService's handle() throwing
        // because the server responded with a non-2xx status (err.status
        // set there). Prompt 9 bug 1: both used to render as the same
        // vague "didn't come back" message, which is exactly what hid a
        // plain "the backend isn't running" behind a scarier-looking error.
        err.unreachable = err instanceof TypeError;
        console.error('[useLessons] fetch failed:', err.message, err);
        if (!cancelled) { setLessons([]); setError(err); }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return { lessons, loading, error };
};

/** name for a lesson id, falling back to a generic label while loading/on miss. */
export const lessonName = (lessons, lessonId) =>
  lessons.find(l => l.id === lessonId)?.title || `Lesson ${lessonId}`;

/**
 * A4 fix: how many reps a lesson actually needs before it counts as
 * "done". Was a hardcoded EXERCISES_PER_LESSON=5 everywhere in
 * PromptLanding.jsx — harmless while every lesson really did have 5 v1
 * exercises, but wrong the moment a lesson has a different real rep
 * count (the v2 gold-standard lessons each have exactly 1 rep today), at
 * which point a passed rep can never push getLessonDone(lid) to 5 and
 * the lesson silently never shows as complete. Falls back to 5 for any
 * lesson not yet loaded/found, matching the old constant's behavior for
 * phases that don't have real lesson rows yet.
 */
export const repCountFor = (lessons, lessonId, fallback = 5) =>
  lessons.find(l => l.id === lessonId)?.rep_count ?? fallback;

/**
 * Prompt 5, item 2: the learner-facing lesson number (1-13) — NOT
 * lessons.id, which is a raw primary key that happens to be scattered
 * (Storytelling occupies ids 4-8 in the same shared table; see
 * backend/scripts/seedAiPromptingV2.js's LESSON_MAP comment). Any UI
 * string showing "Lesson N" must go through this, never lessonId
 * directly. Falls back to the id itself only while lessons hasn't
 * loaded yet, so a node never renders literally blank.
 */
export const lessonNumberFor = (lessons, lessonId) =>
  lessons.find(l => l.id === lessonId)?.lesson_number ?? lessonId;

export default useLessons;
